/**
 * Schedule View — the Provider / Operatory / Status filters.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * ── PER BROWSER, NOT PER OFFICE ──────────────────────────────────────────────────────────────
 * A filter is one person's view of the day, not a practice-wide decision, so it lives in
 * localStorage and never touches the settings blob. The front desk narrowing to one hygienist
 * must not change what the doctor's iPad shows.
 *
 * ── EMPTY MEANS ALL ──────────────────────────────────────────────────────────────────────────
 * Every filter is a list of selected values, and an EMPTY list means "no filter", not "nothing".
 * The alternative — pre-selecting every option — makes a newly added operatory invisible until
 * somebody notices and ticks it, which is exactly the kind of silent omission this page exists to
 * prevent.
 *
 * ── WHY A HIDDEN COUNT IS RETURNED ───────────────────────────────────────────────────────────
 * A filtered schedule looks exactly like a quiet day. `applyFilters` always reports how many
 * appointments it removed so the page can say so out loud; the office must never be able to
 * mistake a filter for an empty morning.
 */

/** localStorage key prefix. Per site, because operatory and provider numbers are site-specific. */
export const FILTER_STORAGE_PREFIX = 'teamio_schedule_view_filters';

/** No filter at all. */
export function emptyFilters() {
  return { providers: [], operatories: [], statuses: [], alerts: [] };
}

export function filterStorageKey(siteId) {
  return `${FILTER_STORAGE_PREFIX}:${siteId || 'unknown'}`;
}

/** Numbers stay numbers; OD ids are numeric and must compare as such. */
function toNumberList(value) {
  if (!Array.isArray(value)) return [];
  const out = [];
  value.forEach((v) => {
    const n = Number(v);
    if (Number.isFinite(n) && out.indexOf(n) === -1) out.push(n);
  });
  return out;
}

function toBucketList(value, allowed) {
  if (!Array.isArray(value)) return [];
  const out = [];
  value.forEach((v) => {
    if (allowed.indexOf(v) !== -1 && out.indexOf(v) === -1) out.push(v);
  });
  return out;
}

const BUCKETS = ['confirmed', 'unconfirmed', 'arrived', 'completed'];

/**
 * Total: takes anything (a parsed localStorage value from an older version, junk, null) and
 * returns a usable filter object. Never throws — a corrupt preference must not break the page.
 */
export function normalizeFilters(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return emptyFilters();
  return {
    providers: toNumberList(raw.providers),
    operatories: toNumberList(raw.operatories),
    statuses: toBucketList(raw.statuses, BUCKETS),
    // Alert keys are not validated against a list here: this file is import-free and the set of
    // alerts grows wave by wave. An unknown key simply matches nothing, which is the same visible
    // outcome as a filter for an alert nobody has.
    alerts: Array.isArray(raw.alerts)
      ? raw.alerts.filter(k => typeof k === 'string' && k)
        .filter((k, i, all) => all.indexOf(k) === i)
      : [],
  };
}

export function hasActiveFilters(filters) {
  const f = normalizeFilters(filters);
  return f.providers.length > 0 || f.operatories.length > 0
    || f.statuses.length > 0 || f.alerts.length > 0;
}

/**
 * Read the remembered filters.
 *
 * `storage` is injected so the tests can pass a fake — and so a browser that throws on
 * localStorage (Safari private browsing does) degrades to "no filter" instead of a blank page.
 */
export function readFilters(storage, siteId) {
  try {
    if (!storage) return emptyFilters();
    const raw = storage.getItem(filterStorageKey(siteId));
    if (!raw) return emptyFilters();
    return normalizeFilters(JSON.parse(raw));
  } catch (e) {
    return emptyFilters();
  }
}

/** Persist. Returns whether it stuck, so a caller can decide whether to promise anything. */
export function writeFilters(storage, siteId, filters) {
  try {
    if (!storage) return false;
    const f = normalizeFilters(filters);
    if (!hasActiveFilters(f)) {
      storage.removeItem(filterStorageKey(siteId));
      return true;
    }
    storage.setItem(filterStorageKey(siteId), JSON.stringify(f));
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Does this appointment involve one of the selected providers?
 *
 * BOTH the treating provider and the hygienist count. An appointment in the hygiene column is the
 * hygienist's appointment as far as the office is concerned, and filtering it out when somebody
 * picks that hygienist would be plainly wrong.
 */
export function matchesProvider(appt, providers) {
  if (!providers || !providers.length) return true;
  if (!appt) return false;
  return providers.indexOf(Number(appt.ProvNum)) !== -1
    || providers.indexOf(Number(appt.ProvHyg)) !== -1;
}

/**
 * One appointment against every filter.
 *
 * @param {Object} appt
 * @param {Object} filters
 * @param {string} bucket     the resolved status bucket (the caller resolves it)
 * @param {Array}  alertKeys  the alert keys this appointment's patient has (caller resolves)
 */
export function matchesFilters(appt, filters, bucket, alertKeys) {
  const f = normalizeFilters(filters);
  if (!appt) return false;
  if (!matchesProvider(appt, f.providers)) return false;
  if (f.operatories.length && f.operatories.indexOf(Number(appt.Op)) === -1) return false;
  if (f.statuses.length && f.statuses.indexOf(bucket) === -1) return false;
  if (f.alerts.length) {
    // ANY of the selected alerts, not all of them: ticking two alerts asks "show me the
    // appointments that need attention", not "show me the unlucky patient who has both".
    const keys = alertKeys || [];
    if (!f.alerts.some(k => keys.indexOf(k) !== -1)) return false;
  }
  return true;
}

/**
 * Apply the filters to a /day payload.
 *
 * @param {Object}   day         the payload
 * @param {Object}   filters
 * @param {Function} bucketOf    appt -> bucket. Injected so this file stays import-free.
 * @param {Function} alertKeysOf appt -> string[]. Also injected, same reason.
 * @returns {{day: Object, hiddenAppointments: number, hiddenOperatories: number}}
 */
export function applyFilters(day, filters, bucketOf, alertKeysOf) {
  if (!day) return { day, hiddenAppointments: 0, hiddenOperatories: 0 };
  const f = normalizeFilters(filters);
  if (!hasActiveFilters(f)) return { day, hiddenAppointments: 0, hiddenOperatories: 0 };

  const resolve = typeof bucketOf === 'function' ? bucketOf : () => 'unconfirmed';
  const alertsOf = typeof alertKeysOf === 'function' ? alertKeysOf : () => [];
  const allAppointments = day.appointments || [];
  const appointments = allAppointments.filter(a => matchesFilters(a, f, resolve(a), alertsOf(a)));

  // ONLY the operatory filter removes columns. A provider or status filter leaves every chair on
  // screen — an empty column is information ("nobody is in room 3 this afternoon"), and columns
  // appearing and disappearing as you toggle a provider makes the day impossible to read.
  const allOperatories = day.operatories || [];
  const operatories = f.operatories.length
    ? allOperatories.filter(op => f.operatories.indexOf(Number(op.OperatoryNum)) !== -1)
    : allOperatories;

  return {
    day: Object.assign({}, day, { appointments, operatories }),
    hiddenAppointments: allAppointments.length - appointments.length,
    hiddenOperatories: allOperatories.length - operatories.length,
  };
}

/** The alerts whose detail carries money worth totalling next to their count. */
export const MONEY_ALERT_KEYS = ['balance', 'claim', 'unsched'];

/**
 * The numbers the Status and Alerts dropdowns print next to their rows (WP-A5, 2026-10-23).
 *
 * ── WHAT IS COUNTED ───────────────────────────────────────────────────────────────────────────
 * The day's appointments narrowed by PROVIDER and OPERATORY only — the statuses and alerts
 * selections are IGNORED. So ticking "Balance" never zeroes the other alert rows (it would, if the
 * rows counted the already-filtered grid: "Recall: 0" next to a ticked Balance reads as "nobody
 * needs a recall"), and every row still says how many appointments ticking it WOULD show. A provider
 * or chair filter does change the numbers: that is a real narrowing of the day the dropdowns describe.
 * (The old summary strip counted the fully filtered day; Nilay replaced it with these dropdowns.)
 *
 * ── THE SHAPE ─────────────────────────────────────────────────────────────────────────────────
 * `summaryCounts()` of that narrowed set — `{ total, statuses, alerts, ran, errors }`, alerts per
 * APPOINTMENT and only for rules in `ran` — plus `totals`: the money behind `balance`, `claim` and
 * `unsched` (`alertTotal`, deduplicated per patient / guarantor), for rules in `ran` only.
 *
 * `appointments` is the UNFILTERED day's; `filters` is the page's full filter state. Neither is
 * mutated. `deps` = `{ summaryCounts, alertTotal }` from alerts.js, passed in because this file is
 * import-free; a caller that forgets them gets a loud error, not a quietly empty dropdown.
 */
export function facetCounts(appointments, filters, payload, bucketOf, deps) {
  const d = deps || {};
  if (typeof d.summaryCounts !== 'function' || typeof d.alertTotal !== 'function') {
    throw new Error('facetCounts needs deps.summaryCounts and deps.alertTotal (alerts.js)');
  }
  const f = normalizeFilters(filters);
  const narrowing = { providers: f.providers, operatories: f.operatories, statuses: [], alerts: [] };
  const resolve = typeof bucketOf === 'function' ? bucketOf : () => 'unconfirmed';
  const narrowed = (appointments || []).filter(a => matchesFilters(a, narrowing, resolve(a), []));

  const counts = d.summaryCounts(narrowed, payload, resolve);
  const totals = {};
  MONEY_ALERT_KEYS.forEach((key) => {
    if (counts.ran.indexOf(key) !== -1) totals[key] = d.alertTotal(narrowed, payload, key);
  });
  return Object.assign({}, counts, { totals });
}

/**
 * The rows of the Status and Alerts dropdowns, with their numbers (see facetCounts).
 *
 * @param {Array}    statusOptions  filterOptions().statuses — [{ value, label }]
 * @param {Object}   counts         facetCounts()
 * @param {Array}    alertMeta      settingsForm.js ALERT_META — [{ key, icon, label }], the page's one
 *                                  list of alerts: its ORDER is the dropdown's order (so the list does
 *                                  not reshuffle when a rule fails), its `label` the words and its
 *                                  `icon` the very icon the alert has on a block
 * @param {Function} formatMoney    alerts.js formatMoney, injected (import-free file)
 * @returns {{statuses: Array, alerts: Array}}  statuses: the options plus `count`; alerts: one row per
 *   alert that RAN — never one for a rule that was not computed ("Medical history due: 0" for every
 *   practice is a confident, permanent lie) — as { value, label, icon, count, total? }; `total` (money
 *   string) only for balance / claim / unsched with something behind them.
 */
export function facetOptions(statusOptions, counts, alertMeta, formatMoney) {
  const c = counts || {};
  const byStatus = c.statuses || {};
  const byAlert = c.alerts || {};
  const totals = c.totals || {};
  const ran = c.ran || [];

  const statuses = (statusOptions || []).map(o => Object.assign({}, o, { count: byStatus[o.value] || 0 }));
  const alerts = (alertMeta || [])
    .filter(meta => meta && ran.indexOf(meta.key) !== -1)
    .map((meta) => {
      const row = { value: meta.key, label: meta.label, icon: meta.icon, count: byAlert[meta.key] || 0 };
      const money = totals[meta.key] > 0 && typeof formatMoney === 'function' ? formatMoney(totals[meta.key]) : '';
      if (money) row.total = money;
      return row;
    });
  return { statuses, alerts };
}

/**
 * The options each filter offers, derived from the day itself.
 *
 * Providers come from the day's OWN appointments, not from the full provider list: a dropdown
 * offering forty providers when six are in today is a worse tool than one offering six.
 */
export function filterOptions(day) {
  if (!day) return { providers: [], operatories: [], statuses: [] };

  const providerIndex = {};
  (day.providers || []).forEach((p) => { if (p) providerIndex[p.ProvNum] = p; });

  const seen = [];
  const providers = [];
  (day.appointments || []).forEach((appt) => {
    [appt.ProvNum, appt.ProvHyg].forEach((provNum) => {
      const n = Number(provNum);
      if (!n || seen.indexOf(n) !== -1) return;
      seen.push(n);
      const p = providerIndex[n];
      if (!p) return;
      providers.push({
        value: n,
        label: p.Abbr || `${p.FName || ''} ${p.LName || ''}`.trim() || `Provider ${n}`,
      });
    });
  });
  providers.sort((a, b) => String(a.label).localeCompare(String(b.label)));

  const operatories = (day.operatories || []).map(op => ({
    value: Number(op.OperatoryNum),
    label: op.OpName || op.Abbrev || `Operatory ${op.OperatoryNum}`,
  }));

  return {
    providers,
    operatories,
    statuses: [
      { value: 'confirmed', label: 'Confirmed' },
      { value: 'unconfirmed', label: 'Unconfirmed' },
      { value: 'arrived', label: 'Arrived' },
      { value: 'completed', label: 'Completed' },
    ],
  };
}
